import {FormLabel, Grid} from '@mui/material';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import _ from 'lodash';
import clsx from 'clsx';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import React, {useEffect, useState} from 'react';
import MessageBox from '../../../../util/messageBox';
import {useDispatch, useSelector} from 'react-redux';
import {loadWardPatientsByWardId, onlyActiveSingleWards, updatePatientBedNew} from '../../../../store/reducers/ward';
import useDict from '../../../../hooks/useDict';
import {useTranslation} from 'react-i18next';
import {useStylesEscalationLevel} from '../escalationLevel';
import API from '../../../../app/api';
import {setSnackbar} from '../../../../store/reducers/globals';
import {sendUserAudit} from '../../../../store/reducers/user';
import useOpenState from '../../../../hooks/useOpenState';
import {setLoading as setLoadingGlobal} from '../../../../store/reducers/patient';
import {getWardsIdForPatientsSearch} from '../../../../store/reducers/wardMap';
import { AUDIT_TYPES } from '../../../../util/auditUtil';

const EscalationChange = ({levels, reloadData, currentWard, fromAdminPage}) => {
    const {t} = useTranslation();
    const classes = useStylesEscalationLevel();
    const dispatch = useDispatch();

    // Selectors
    const wards = useSelector(onlyActiveSingleWards);

    // Dict
    const [mapTypes, mapTypesLoading] = useDict('MapTypes');

    // States
    const [selectedWard, setSelectedWard] = useState(currentWard);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [reason, setReason] = useState('');
    const [isSaveConfirmOpen, openSaveConfirm, closeSaveConfirm] = useOpenState(false, true);

    // Bed Occupation options
    const [isOptionsOpen, openOptions, closeOptions] = useOpenState(false, true);
    const [ptToUpdate, setPtToUpdate] = useState([]);

    // Bed Request options
    const [isBedRequestsOpen, openBedRequests, closeBedRequests] = useOpenState(false, true);
    const [brToUpdate, setBrToUpdate] = useState([]);


    useEffect(() => {
        if (fromAdminPage && selectedWard) {
            dispatch(loadWardPatientsByWardId(dispatch(getWardsIdForPatientsSearch(selectedWard.wardsId, selectedWard.hospitalId)), true));
        }
    }, [fromAdminPage, selectedWard]);

    const handleChangeWard = (_e, val) => {
        setSelectedWard(val);
    };

    const handleChangeLevel = (_e, val) => {
        setSelectedLevel(val);
    };

    const handleCheckBedOccupations = async () => {
        closeSaveConfirm();

        const find = _.find(levels, {wardId: selectedWard?.wardsId[0]});
        const level = find?.level || 'core';
        const levelObj = _.find(mapTypes, {__code: level}) || null;
        if (level !== selectedLevel?.__code) {
            dispatch(setLoadingGlobal(true));
            let decrease = false;
            if (levelObj?.val > selectedLevel?.val) {
                decrease = true;
            }
            // If decrease the check for bed occupations
            if (decrease) {
                await API.getWardsAttendances(selectedWard?.wardsId)
                    .then(async r => {
                        const ptArr = [];
                        _.forEach(r, pt => {
                            if (pt) {
                                let elementHidden = false;
                                if (_.startsWith(pt?.bed, 'wlist') || !pt?.bed) {
                                    if (pt?.admission?.locationDescription === 'DISCHARGE') {
                                        elementHidden = true;
                                    }
                                }

                                if (!elementHidden) ptArr.push(pt);
                            }
                        });

                        let beds;
                        if (!fromAdminPage) {
                            beds = selectedWard?.beds;
                        } else {
                            // Load all beds and areas
                            await API.getAllBedsAndAreas(selectedWard.code)
                                .then(async bedArea => {
                                    const mapObj = await createMapObject(selectedWard, bedArea?.data);
                                    beds = mapObj.beds;
                                })
                                .catch(err => {
                                    console.error(err);
                                    dispatch(setSnackbar({
                                        type: 'error',
                                        message: 'There was a problem retrieving the ward map object.',
                                    }));
                                    beds = [];
                                });
                        }

                        const putsOccupied = [];

                        _.forEach(beds, bed => {
                            if (checkForBed(bed.subtype, level, selectedLevel?.__code)) {
                                const pts = _.filter(ptArr, {bed: bed.__id});
                                if (!_.isEmpty(pts)) putsOccupied.push(...pts);
                            }
                        });
                        if (!_.isEmpty(putsOccupied)) {
                            setPtToUpdate(putsOccupied);
                            openOptions();
                        } else {
                            const bedsDeescalate = _.filter(beds, b => checkForBed(b.subtype, level, selectedLevel?.__code))
                            const bedIds = bedsDeescalate.map(b => b.__id);
                            // Ged Bed request
                            await API.getBedRequest(`status eq 'NEW'`)
                                .then(async res => {
                                    const filtered = _.filter(res?.__data, br => bedIds.includes(br?.destination?.bedId));
                                    if (!_.isEmpty(filtered)) {
                                        setBrToUpdate(filtered);
                                        openBedRequests();
                                    } else {
                                        handleSubmitLevel();
                                    }
                                })
                                .catch(err => {
                                    console.error(err);
                                    dispatch(setSnackbar({type: 'error', message: t('Unable to retrieve bed requests.')}));
                                })
                        }
                    })
                    .catch(error => {
                        console.error(error);
                    })
                    .finally(() => {
                        dispatch(setLoadingGlobal(false));
                    });
            } else {
                dispatch(setLoadingGlobal(false));
                handleSubmitLevel();
            }
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('You cannot select the same level.')}));
        }
    };

    const checkForBed = (bedType, currentLevel, selectedLevel) => {
        if (selectedLevel === 'core') {
            return currentLevel === 'unplanned' ? (bedType === 'unplanned' || bedType === 'planned') : bedType === 'planned';
        } else if (selectedLevel === 'planned') {
            return bedType === 'unplanned';
        }
        return false;
    };

    const handleSubmitLevel = () => {
        const find = _.find(levels, {wardId: selectedWard?.wardsId[0]});
        const data = {
            wardId: selectedWard?.wardsId[0],
            reason,
            level: selectedLevel?.__code,
            hospitalId: selectedWard?.hospitalId,
            __id: find?.__id || null,
        };

        const auditData = {
            ward: selectedWard?.name,
            mapId: selectedWard?.__id,
            wardId: selectedWard?.wardsId[0],
            hospitalId: selectedWard?.hospitalId,
            reason,
            level: selectedLevel?.__code,
        };

        API.updateEscalationLevel(data)
            .then(res => {
                dispatch(setSnackbar({type: 'success', message: t('Data successfully saved.')}));
                dispatch(sendUserAudit(null, AUDIT_TYPES.ESCALATION_LEVEL, auditData));

                reloadData();
            })
            .catch(() => {
                dispatch(setSnackbar({type: 'error', message: t('Unable to save escalation levels.')}));
            })
            .finally(() => {
                setSelectedWard(null);
                setSelectedLevel(null);
                setReason('');
                closeSaveConfirm();
            });
    };

    const handeUpdatePtAndChangeLevel = async () => {
        dispatch(setLoadingGlobal(true));

        for (const pt of ptToUpdate) {
            await dispatch(updatePatientBedNew(pt.__id, null));
        }

        dispatch(setLoadingGlobal(false));

        handleCloseOptions();
        handleSubmitLevel();
    };

    const handeCancelBedRequestsAndChangeLevel = async () => {
        dispatch(setLoadingGlobal(true));

        for (const br of brToUpdate) {
            const brCancel = _.cloneDeep(br);
            brCancel.status = 'CANCELLED';
            delete brCancel.__href;
            delete brCancel.__type;
            delete brCancel.__version;
            delete brCancel._createdBy;
            delete brCancel._createdDate;
            await API.saveBedRequest(brCancel)
        }

        dispatch(setLoadingGlobal(false));

        handleCloseBrOptions();
        handleSubmitLevel();
    };

    const handleCloseOptions = () => {
        setPtToUpdate([]);
        closeOptions();
    };

    const handleCloseBrOptions = () => {
        setBrToUpdate([]);
        closeBedRequests()
    };

    const getLevel = () => {
        if (selectedWard) {
            const find = _.find(levels, {wardId: selectedWard?.wardsId[0]});
            const level = find?.level || 'core';

            return (
                <div className={clsx(classes.escalationLevel, level)}>
                    {_.capitalize(level)}
                </div>
            );
        }
    };

    const handleChangeReason = (e) => {
        const {value} = e.target;
        setReason(value);
    };

    return (
        <>
            <div className={classes.container} style={{marginTop: 10}}>
                <FormLabel className={classes.bold}>
                    {t('Change Escalation Level')}
                </FormLabel>

                <div className={classes.row}>
                    <AgyleDictionaryField
                        label={t('Ward Map')}
                        onChange={handleChangeWard}
                        options={wards}
                        value={selectedWard}
                        getOptionLabel={option => option.name}
                        isOptionEqualToValue={(option, value) => option.__id === value.__id}
                        disabled={!!currentWard}
                    />
                </div>

                {selectedWard &&
                    <Grid container direction="row" className={classes.row}>
                        <Grid item xs={12} className={classes.divRow} style={{gap: 10}}>
                            <FormLabel className={classes.bold}>
                                {t('Current selected ward escalation level')}
                            </FormLabel>

                            {getLevel()}
                        </Grid>

                        <div className={classes.row} style={{width: '100%'}}>
                            <AgyleDictionaryField
                                label={('Escalation Level')}
                                options={mapTypes}
                                onChange={handleChangeLevel}
                                value={selectedLevel}
                                loading={mapTypesLoading}
                                getOptionLabel={option => option.__description}
                                disableClearable
                            />
                        </div>

                        <div className={classes.row} style={{width: '100%'}}>
                            <AgyleTextField
                                label={('Change escalation level reason')}
                                value={reason}
                                onChange={handleChangeReason}
                                fullWidth
                                error={_.isEmpty(reason)}
                            />
                        </div>

                        <div className={clsx(classes.row, 'right')}>
                            <AgyleButton
                                variant="contained"
                                disabled={!selectedLevel || !selectedWard || _.isEmpty(reason)}
                                onClick={openSaveConfirm}
                            >
                                {t('Submit')}
                            </AgyleButton>
                        </div>
                    </Grid>
                }
            </div>

            <MessageBox
                open={isSaveConfirmOpen}
                onClose={closeSaveConfirm}
                title={t('Confirmation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleCheckBedOccupations}
                message={t('Are you sure you want to change escalation level?')}
            />

            <MessageBox
                open={isOptionsOpen}
                onClose={handleCloseOptions}
                title={t('Information')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handeUpdatePtAndChangeLevel}
                message={t('There are patients on the beds in Escalation level, do you want to move them to the Patient Lobby?')}
            />

            <MessageBox
                open={isBedRequestsOpen}
                onClose={handleCloseBrOptions}
                title={t('Information')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handeCancelBedRequestsAndChangeLevel}
                message={t('There are bed requests for the beds in Escalation level, do you want to cancel them?')}
            />
        </>
    );
};

export default EscalationChange;

const createMapObject = (wardMap, bedAndAreas) => {
    let obj = null;
    if (wardMap && bedAndAreas) {
        obj = {
            ...wardMap,
            areas: [],
            beds: [],
        };
        _.forEach(bedAndAreas, ba => {

            obj.areas = [...obj.areas, ...ba.areas];
            obj.beds = [...obj.beds, ...ba.beds];
        });
        obj.areas = [...obj.areas];
    }
    return obj;
};